import { ActionType, IPlacement, ReferenceMap } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import {
    ReferralSourcePicker,
    toMutationInput,
} from "components/references/select-reference/referral-source-picker";
import { trpc } from "components/rpc/trpc";
import { IsAllowed } from "components/shared/is-allowed";
import { AutosavingInput } from "components/ui/autosaving-input";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { Action, Dispatch } from "redux";
import { Actions } from "store/actions";

interface PlacementReferralSourceProps {
    placement: IPlacement;
    onFieldChange: (fieldName: string, newValue: string) => void;
}

export const PlacementReferralSource: React.FC<PlacementReferralSourceProps> = props => {
    const { placement, onFieldChange } = props;
    const dispatch = useDispatch<Dispatch<Action>>();
    const trpcClient = trpc.useTRPC();

    const setReferralSource = useMutation({
        ...trpcClient.clients.setReferralSource.mutationOptions(),
        onSuccess: data => {
            dispatch({
                type: Actions[Actions.UPDATE_CLIENT_REFERRAL_SOURCE],
                clientId: placement.id,
                version: data.version,
                referralBusinessId: data.referralBusinessId,
                referralContactId: data.referralContactId,
                referralSourceToken: data.referralSourceToken,
            });
        },
    });

    const showNotes = useMemo(() => {
        return (
            placement.referralSourceToken ||
            placement.referralContactId ||
            placement.referralBusinessId ||
            typeof placement.referralNotes === "string"
        );
    }, [placement]);

    return (
        <IsAllowed actionType={ActionType.ViewReference}>
            <InputFormBlock>
                <div className="mb-2">
                    <ReferralSourcePicker
                        parentEntityType={AdvancedSearchEntityType.Client}
                        parentId={placement.id}
                        value={placement.referralSourceMap}
                        onChange={(newValue: ReferenceMap) => {
                            setReferralSource.mutate({
                                clientId: placement.id,
                                source: toMutationInput(newValue),
                            });
                        }}
                    />
                </div>
                {showNotes ? (
                    <div className="form-group">
                        <AutosavingInput
                            multiLine={true}
                            label="Referral Notes (optional)"
                            initial={placement.referralNotes}
                            onCommit={newVal => onFieldChange("referralNotes", newVal)}
                        />
                    </div>
                ) : null}
            </InputFormBlock>
        </IsAllowed>
    );
};
